import { apiFetch, qs } from '../lib/api';

export type MatchSource = 'canonical' | 'alias' | 'tolerance';

export interface Vehicle {
  id: number;
  year: number;
  make: string;
  make_slug: string;
  make_raw: string;
  model: string;
  model_slug: string;
  model_raw: string;
  submodel: string | null;
  drive_type: string | null;
  body_config: string | null;
  bolt_pattern_raw: string;
  bolt_pattern_normalized: string;
  bolt_pattern_canonical: string;
  bolt_pattern_match_source: MatchSource | null;
  thread_pitch: string | null;
  hub_bore_front_mm: number | null;
  hub_bore_rear_mm: number | null;
  front_offset_min_mm: number | null;
  front_offset_max_mm: number | null;
  rear_offset_min_mm: number | null;
  rear_offset_max_mm: number | null;
  wheel_diameter_min_in: number | null;
  wheel_diameter_max_in: number | null;
  front_width_max_in: number | null;
  rear_width_max_in: number | null;
  oem_tire_sizes_front: string[];
  oem_tire_sizes_rear: string[];
  plus_size_recommendations: Record<string, string>;
  tpms_sensor: string | null;
  fitment_notes: string | null;
  source_guide: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VehicleListParams {
  year?: number;
  make?: string;
  model?: string;
  bolt_pattern?: string;
  q?: string;
  include_inactive?: boolean;
  limit?: number;
  offset?: number;
}

export interface VehicleListResponse {
  data: Vehicle[];
  meta: { total: number; limit: number; offset: number };
}

export function listVehicles(params: VehicleListParams = {}): Promise<VehicleListResponse> {
  return apiFetch(`/admin/vehicles${qs(params as Record<string, unknown>)}`);
}

export interface VehicleFilterOptions {
  years: number[];
  makes: Array<{ make_slug: string; make: string; count: number }>;
  models: Array<{ model_slug: string; model: string; make_slug: string; count: number }>;
  bolt_patterns: Array<{ bolt_pattern_canonical: string; count: number }>;
}

export function getVehicleFilterOptions(makeSlug?: string): Promise<VehicleFilterOptions> {
  const q = makeSlug ? `?make=${encodeURIComponent(makeSlug)}` : '';
  return apiFetch(`/admin/vehicles/filter-options${q}`);
}

export function getVehicle(id: number): Promise<Vehicle> {
  return apiFetch(`/admin/vehicles/${id}`);
}

export type VehicleUpdate = Partial<Omit<Vehicle,
  'id' | 'make_slug' | 'model_slug' | 'make_raw' | 'model_raw' |
  'bolt_pattern_normalized' | 'bolt_pattern_canonical' | 'bolt_pattern_match_source' |
  'is_active' | 'created_at' | 'updated_at'
>>;

export function updateVehicle(id: number, patch: VehicleUpdate): Promise<Vehicle> {
  return apiFetch(`/admin/vehicles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function createVehicle(payload: VehicleUpdate & { year: number; make: string; model: string; bolt_pattern_raw: string }): Promise<Vehicle> {
  return apiFetch(`/admin/vehicles`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function deleteVehicle(id: number): Promise<void> {
  return apiFetch(`/admin/vehicles/${id}`, { method: 'DELETE' });
}
